import { Document, SearchFilters, ProcessingStatus, CustomField, AuditLogEntry, OneTimeAction, OneTimeActionRunSummary, ExtractionChoice } from '../types';
import { ThrottleAction } from '../types/user';
import { s3Service } from './s3Service';
import { dynamoDBService } from './dynamoDBService';
import { throttleService } from './throttleService';
import { authService } from './authService';
import { processImage, generateThumbnail, isImageFile, isPDFFile, countPdfPages } from '../utils/imageProcessor';
import { apiClient, apiEndpoints } from './apiClient';
import {
  isAllowedUploadFile,
  SUPPORTED_UPLOAD_TYPES_LABEL,
  MAX_UPLOAD_FILE_SIZE_MB,
} from '../constants/uploadAccept';
import { TEXTRACT_OCR_MAX_WAIT_MS } from '../constants/ocrLimits';
import { buildDocumentSearchableText } from '../utils/searchableTextBuilder';
import { sha256Hex } from '../utils/fileHash';
import {
  billMatches,
  DuplicateCheckResult,
  DuplicateMatch,
  requestDuplicateDecision,
} from '../utils/duplicateDecision';
import { UploadCancelledError } from '../utils/uploadCancelled';

export type DuplicateUploadContext = {
  contentHash?: string;
  pageCount?: number;
  ewayBillNumbers?: string[];
  documentNumbers?: string[];
  invoiceNumbers?: string[];
  podNumbers?: string[];
  /** True when the user already continued past a matching number. */
  duplicateOverride?: boolean;
};

// Check if API Gateway is configured
const isApiGatewayConfigured = (): boolean => {
  return !!process.env.REACT_APP_API_URL;
};

// Production mode: Always use AWS services

// Helper to convert File to Uint8Array (browser-compatible)
const fileToBuffer = (file: File): Promise<Uint8Array> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const arrayBuffer = reader.result as ArrayBuffer;
      if (!arrayBuffer || arrayBuffer.byteLength === 0) {
        reject(new Error('File buffer is empty'));
        return;
      }
      const uint8Array = new Uint8Array(arrayBuffer);
      resolve(uint8Array);
    };
    reader.onerror = (error) => {
      reject(new Error(`Failed to read file: ${error}`));
    };
    reader.readAsArrayBuffer(file);
  });
};

type ProcessDocumentApiResponse = {
  message?: string;
  ocrAsync?: boolean;
  textractJobId?: string;
  /** sns = SNS→Lambda + EventBridge; poll = HTTP GetDocumentAnalysis fallback */
  ocrCompletionMode?: 'sns' | 'poll';
  extractedData?: Document['extractedData'];
  duplicatePrompt?: boolean;
  matches?: DuplicateMatch[];
};

const emptyDuplicateCheck = (): DuplicateCheckResult => ({
  matches: [],
  ewayBillNumbers: [],
  documentNumbers: [],
  invoiceNumbers: [],
  podNumbers: [],
});

async function checkDuplicateRequest(input: {
  contentHash?: string;
  originalFileName?: string;
  originalByteSize?: number;
  pageCount?: number;
  pdfText?: string;
  fields?: { key: string; value: string }[];
  ewayBillNumbers?: string[];
  documentNumbers?: string[];
  invoiceNumbers?: string[];
  podNumbers?: string[];
  excludeDocumentId?: string;
}): Promise<DuplicateCheckResult> {
  if (!isApiGatewayConfigured()) return emptyDuplicateCheck();
  const response = await apiClient.post<DuplicateCheckResult>(apiEndpoints.documents.duplicateCheck(), input);
  if (response.error || !response.data) {
    console.warn('Duplicate check failed', response.error);
    return emptyDuplicateCheck();
  }
  return {
    matches: Array.isArray(response.data.matches) ? response.data.matches : [],
    ewayBillNumbers: Array.isArray(response.data.ewayBillNumbers) ? response.data.ewayBillNumbers : [],
    documentNumbers: Array.isArray(response.data.documentNumbers) ? response.data.documentNumbers : [],
    invoiceNumbers: Array.isArray(response.data.invoiceNumbers) ? response.data.invoiceNumbers : [],
    podNumbers: Array.isArray(response.data.podNumbers) ? response.data.podNumbers : [],
  };
}

function processFailure(processResponse: { error?: string; details?: unknown }): Error {
  const details = processResponse.details as { status?: number } | undefined;
  const is408 = details?.status === 408;
  const is413 = details?.status === 413;
  const is422 = details?.status === 422;
  return new Error(
    is408
      ? processResponse.error || 'OCR timed out after 2 minutes. Try a smaller document or try again later.'
      : is413
        ? 'This document exceeds the size limit for OCR. Please use a smaller or compressed file and try again.'
        : is422
          ? processResponse.error || 'This file could not be processed by OCR. Try a different export or format.'
          : processResponse.error || 'OCR failed for this document. Please try again.'
  );
}

type TextractStatusApiResponse = {
  jobStatus: 'IN_PROGRESS' | 'SUCCEEDED';
  message?: string;
  extractedData?: Document['extractedData'];
};

const PDF_OCR_POLL_INTERVAL_MS = 2000;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

async function waitForPdfTextract(
  documentId: string,
  onProgress: (message: string) => void
): Promise<Document['extractedData']> {
  const deadline = Date.now() + TEXTRACT_OCR_MAX_WAIT_MS;
  while (Date.now() < deadline) {
    const res = await apiClient.get<TextractStatusApiResponse>(apiEndpoints.documents.textractStatus(documentId));
    if (res.error) {
      throw new Error(res.error);
    }
    const d = res.data;
    if (!d) {
      throw new Error('No response from OCR status.');
    }
    if (d.jobStatus === 'SUCCEEDED' && d.extractedData) {
      return d.extractedData;
    }
    if (d.jobStatus === 'IN_PROGRESS') {
      onProgress(d.message || 'Running OCR on PDF...');
      await sleep(PDF_OCR_POLL_INTERVAL_MS);
      continue;
    }
    throw new Error(d.message || 'OCR failed or returned an unexpected status.');
  }
  throw new Error(
    'OCR is taking longer than expected. Open the document again in a few minutes or contact support if the problem continues.'
  );
}

type AbortIncompleteUploadResponse = {
  message?: string;
  removedDynamoDbRecord?: boolean;
  removedS3Keys?: string[];
};

async function performUploadFailureCleanup(ctx: {
  documentId: string;
  finalS3Key: string;
  thumbnailKey?: string;
  documentRowCreated: boolean;
  failureSummary: string;
}): Promise<string> {
  const hadDb = ctx.documentRowCreated;
  const hadS3 = !!ctx.finalS3Key;

  if (!hadDb && !hadS3) {
    return 'Nothing had been stored yet, so no cleanup was required.';
  }

  if (isApiGatewayConfigured()) {
    const res = await apiClient.post<AbortIncompleteUploadResponse>(
      apiEndpoints.documents.abortIncompleteUpload(ctx.documentId),
      {
        s3Key: ctx.finalS3Key || undefined,
        thumbnailKey: ctx.thumbnailKey,
        failureSummary: ctx.failureSummary,
      }
    );
    if (res.error) {
      throw new Error(res.error);
    }
    const d = res.data;
    const keys = (d?.removedS3Keys || []).filter(Boolean);
    const actions: string[] = [];
    if (d?.removedDynamoDbRecord) {
      actions.push('removed the incomplete document record from the database');
    }
    if (keys.length > 0) {
      actions.push(`deleted partial file(s) from cloud storage (${keys.join(', ')})`);
    }
    if (actions.length === 0) {
      return 'Cleanup ran; if you still see this document listed, remove it manually or contact support.';
    }
    return `To avoid conflicts and orphaned data, we ${actions.join(' and ')}.`;
  }

  if (hadDb) {
    try {
      await dynamoDBService.deleteDocument(ctx.documentId);
    } catch (e) {
      console.warn('Local cleanup: DynamoDB delete failed', e);
    }
  }
  if (ctx.finalS3Key) {
    try {
      await s3Service.deleteFile(ctx.finalS3Key, 'pending');
    } catch (e) {
      console.warn('Local cleanup: S3 delete main failed', e);
    }
  }
  if (ctx.thumbnailKey) {
    try {
      await s3Service.deleteFile(ctx.thumbnailKey, 'pending');
    } catch (e) {
      console.warn('Local cleanup: S3 delete thumbnail failed', e);
    }
  }
  const parts: string[] = [];
  if (hadDb) parts.push('removed the local document record');
  if (hadS3) parts.push('removed pending file(s) from local S3');
  return `To avoid orphaned data, we ${parts.join(' and ')}.`;
}

// Helper to generate unique document ID
const generateDocumentId = (): string => {
  return `doc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Helper to generate S3 key
const generateS3Key = (fileName: string, documentId: string): string => {
  const extension = fileName.split('.').pop() || 'png';
  return `${documentId}.${extension}`;
};

// Helper to check and enforce throttling
const checkThrottle = async (action: ThrottleAction): Promise<void> => {
  const currentUser = await authService.getCurrentUser();
  if (!currentUser) {
    throw new Error('User not authenticated');
  }

  const throttleStatus = throttleService.checkThrottle(currentUser.id, action);
  if (throttleStatus.isThrottled) {
    const resetTime = new Date(throttleStatus.resetAt).toLocaleString();
    throw new Error(
      `Rate limit exceeded for ${action}. Maximum ${throttleStatus.maxRequests} requests per ${throttleStatus.timeWindowSeconds} seconds. Try again after ${resetTime}`
    );
  }

  // Increment counter
  throttleService.incrementThrottle(currentUser.id, action);
};

type DocumentListResponse = { documents: Document[]; nextToken?: string };

export const apiService = {
  /**
   * Get documents with optional filters
   */
  async getDocuments(
    filters?: SearchFilters,
    options?: { limit?: number; nextToken?: string }
  ): Promise<DocumentListResponse> {
    if (isApiGatewayConfigured()) {
      // Use API Gateway
      const params: {
        status?: string;
        search?: string;
        limit?: number;
        nextToken?: string;
        fieldFilters?: Record<string, string>;
        dateFrom?: string;
        dateTo?: string;
      } = {};
      if (filters?.status) {
        // Handle array or single status - API now supports comma-separated values
        params.status = Array.isArray(filters.status) 
          ? filters.status.join(',') 
          : filters.status;
      }
      if (filters?.query) params.search = filters.query;
      if (options?.limit) params.limit = options.limit;
      if (options?.nextToken) params.nextToken = options.nextToken;
      if (filters?.fieldFilters && Object.keys(filters.fieldFilters).length > 0) {
        params.fieldFilters = filters.fieldFilters;
      }
      if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters?.dateTo) params.dateTo = filters.dateTo;
      
      const response = await apiClient.get<DocumentListResponse>(
        apiEndpoints.documents.list(params)
      );
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      return {
        documents: response.data?.documents || [],
        nextToken: response.data?.nextToken,
      };
    } else {
      // Fallback to direct DynamoDB
      const docs = await dynamoDBService.queryDocuments(filters);
      return { documents: docs, nextToken: undefined };
    }
  },

  /**
   * Get a single document by ID
   */
  async getDocument(id: string): Promise<Document | null> {
    await checkThrottle('view');
    
    if (isApiGatewayConfigured()) {
      // Use API Gateway
      const response = await apiClient.get<Document>(apiEndpoints.documents.get(id));
      
      if (response.error) {
        if (response.error.includes('not found') || response.error.includes('404')) {
          return null;
        }
        throw new Error(response.error);
      }
      
      return response.data || null;
    } else {
      // Fallback to direct DynamoDB
      return await dynamoDBService.getDocument(id);
    }
  },

  /**
   * Upload and process a document
   * IMPORTANT: This is the ONLY place where Textract is triggered (via backend process + PDF polling).
   * Textract should NEVER be called during edit/update operations.
   * Once extracted, the extractedData is stored and reused.
   */
  checkDuplicate: checkDuplicateRequest,

  async uploadDocument(
    file: File,
    extractionChoice: ExtractionChoice,
    duplicate?: DuplicateUploadContext
  ): Promise<{ documentId: string; status: ProcessingStatus; document?: Document }> {
    await checkThrottle('add');

    if (!isAllowedUploadFile(file)) {
      throw new Error(`Unsupported file type. Please upload ${SUPPORTED_UPLOAD_TYPES_LABEL} files only.`);
    }

    const documentId = generateDocumentId();

    let finalS3Key = '';
    let thumbnailKey: string | undefined;
    let documentRowCreated = false;

    try {
    let contentHash = duplicate?.contentHash;
    if (!contentHash) {
      try {
        contentHash = await sha256Hex(file);
      } catch (hashError) {
        console.warn('Could not hash the file before upload', hashError);
      }
    }
    let billNumbersAcknowledged = duplicate?.duplicateOverride === true;

    const status: ProcessingStatus = {
      documentId,
      stage: 'uploading',
      progress: 0,
      message: 'Uploading file...',
    };

    // Process image (resize, convert to PNG, optimize)
    status.stage = 'processing';
    status.progress = 20;
    status.message = 'Processing image...';
    
    let processedFile: File;
    let pageCount = duplicate?.pageCount && duplicate.pageCount > 0 ? duplicate.pageCount : 1;
    let pdfPagesMeasured = false;
    if (isImageFile(file)) {
      // Process image: resize to 1600x2300px, convert to PNG, optimize to 300-800KB
      const processed = await processImage(file);
      // Create new File object from processed blob
      const fileName = file.name.replace(/\.[^/.]+$/, '') + '.png';
      processedFile = new File([processed.blob], fileName, { type: 'image/png' });
      status.progress = 40;
      status.message = `Image processed: ${(processed.size / 1024).toFixed(0)}KB`;
    } else if (isPDFFile(file)) {
      // For PDFs, keep as-is (PDF processing would require PDF.js)
      processedFile = file; // Keep original PDF
      status.progress = 40;
      status.message = 'PDF ready';
    } else {
      // Unknown format, use as-is
      processedFile = file;
      status.progress = 40;
      status.message = 'File ready';
    }

    // Upload processed file to S3
    status.stage = 'uploading';
    status.progress = 50;
    status.message = 'Uploading to S3...';
    finalS3Key = generateS3Key(processedFile.name, documentId);
    
    if (isApiGatewayConfigured()) {
      // Declare the size of the object we will PUT (processed file), for server validation consistency
      const fileSizeToSend =
        typeof processedFile.size === 'number' && !isNaN(processedFile.size) && processedFile.size > 0
          ? processedFile.size
          : typeof file.size === 'number' && !isNaN(file.size) && file.size > 0
            ? file.size
            : 0;
      
      if (!fileSizeToSend || fileSizeToSend <= 0) {
        throw new Error(`Invalid file size for ${file.name}. The file may be corrupted or empty.`);
      }
      
      // Use presigned URLs for upload (production - no AWS credentials in frontend)
      const uploadUrlResponse = await apiClient.post<{ url: string; key: string }>(
        apiEndpoints.documents.uploadUrl(),
        {
          key: finalS3Key,
          folder: 'pending',
          contentType: processedFile.type || (isPDFFile(processedFile) ? 'application/pdf' : 'image/png'),
          fileSize: fileSizeToSend, // bytes of the body we upload (processed file)
        }
      );
      
      if (uploadUrlResponse.error || !uploadUrlResponse.data) {
        const errorMsg = uploadUrlResponse.error || uploadUrlResponse.details?.error || 'Failed to get upload URL. Please try again.';
        throw new Error(errorMsg);
      }
      
      // Upload file directly to S3 using presigned URL
      // Note: The presigned URL includes signed headers (x-amz-server-side-encryption)
      // We must NOT override those headers, only add Content-Type if not already in URL
      const fileBuffer = await fileToBuffer(processedFile);
      const uploadResponse = await fetch(uploadUrlResponse.data.url, {
        method: 'PUT',
        body: fileBuffer as any, // Uint8Array is compatible with BodyInit at runtime
        headers: {
          'Content-Type': processedFile.type || (isPDFFile(processedFile) ? 'application/pdf' : 'image/png'),
          // Do NOT include x-amz-server-side-encryption here - it's already in the signed URL
        },
      });
      
      if (!uploadResponse.ok) {
        let errorMsg = 'Failed to upload file to storage.';
        if (uploadResponse.status === 403) {
          errorMsg = 'Upload failed: Permission denied. Please try again or contact support.';
        } else if (uploadResponse.status === 413) {
          errorMsg = `Upload failed: File is too large. Maximum file size is ${MAX_UPLOAD_FILE_SIZE_MB}MB.`;
        } else if (uploadResponse.status >= 500) {
          errorMsg = 'Upload failed: Server error. Please try again in a few moments.';
        } else {
          errorMsg = `Upload failed: ${uploadResponse.statusText || 'Unknown error'}. Please try again.`;
        }
        throw new Error(errorMsg);
      }
    } else {
      // Direct S3 upload (local development with AWS credentials)
      await s3Service.uploadFile(processedFile, finalS3Key, 'pending');
    }

    // Generate and upload thumbnail for images and PDFs (creates ~8-15KB thumbnail vs 300-800KB full image)
    // Thumbnails: 200px width, Progressive JPEG format for faster loading
    if (isImageFile(file)) {
      try {
        status.progress = 55;
        status.message = 'Generating thumbnail...';
        const thumbnail = await generateThumbnail(processedFile);
        // JPEG bytes must use a .jpg key. A .png key is rejected because it does not match image/jpeg.
        const nextThumbnailKey = `thumb_${finalS3Key.replace(/\.(png|jpe?g|heic|heif)$/i, '.jpg')}`;
        const thumbnailFile = new File(
          [thumbnail.blob],
          `thumb_${processedFile.name.replace(/\.(png|jpe?g|heic|heif)$/i, '.jpg')}`,
          { type: 'image/jpeg' }
        );
        
        if (isApiGatewayConfigured()) {
          // Use presigned URL for thumbnail upload
          // Ensure thumbnail fileSize is valid
          if (!thumbnailFile.size || thumbnailFile.size <= 0) {
            throw new Error('Invalid thumbnail file size. Thumbnail generation may have failed.');
          }
          
          const thumbUrlResponse = await apiClient.post<{ url: string; key: string }>(
            apiEndpoints.documents.uploadUrl(),
            {
              key: nextThumbnailKey,
              folder: 'pending',
              contentType: 'image/jpeg',
              fileSize: thumbnailFile.size, // Required by backend validation
            }
          );
          
          if (thumbUrlResponse.error || !thumbUrlResponse.data) {
            throw new Error(thumbUrlResponse.error || 'Failed to get presigned upload URL for thumbnail');
          }
          
          const thumbBuffer = await fileToBuffer(thumbnailFile);
          const thumbUploadResponse = await fetch(thumbUrlResponse.data.url, {
            method: 'PUT',
            body: thumbBuffer as any, // Uint8Array is compatible with BodyInit at runtime
            headers: {
              'Content-Type': 'image/jpeg',
            },
          });
          
          if (!thumbUploadResponse.ok) {
            throw new Error(`Failed to upload thumbnail to S3: ${thumbUploadResponse.statusText}`);
          }
        } else {
          // Direct S3 upload for thumbnail
          await s3Service.uploadFile(thumbnailFile, nextThumbnailKey, 'pending');
        }

        thumbnailKey = nextThumbnailKey;
        status.progress = 60;
        status.message = `Thumbnail: ${(thumbnail.size / 1024).toFixed(0)}KB`;
      } catch (error) {
        console.warn('Failed to generate thumbnail:', error);
        // Continue without thumbnail - not critical
      }
    } else if (isPDFFile(file)) {
      try {
        status.progress = 55;
        status.message = 'Generating PDF thumbnail...';
        const { generatePDFThumbnail } = await import('../utils/imageProcessor');
        const thumbnail = await generatePDFThumbnail(file);
        if (thumbnail.pageCount && thumbnail.pageCount > 0) {
          pageCount = thumbnail.pageCount;
          pdfPagesMeasured = true;
        }
        thumbnailKey = `thumb_${finalS3Key.replace(/\.pdf$/i, '.jpg')}`;
        const thumbnailFile = new File([thumbnail.blob], `thumb_${processedFile.name.replace(/\.pdf$/i, '.jpg')}`, { type: 'image/jpeg' });
        
        if (isApiGatewayConfigured()) {
          // Use presigned URL for thumbnail upload
          // Ensure thumbnail fileSize is valid
          if (!thumbnailFile.size || thumbnailFile.size <= 0) {
            throw new Error('Invalid thumbnail file size. Thumbnail generation may have failed.');
          }
          
          const thumbUrlResponse = await apiClient.post<{ url: string; key: string }>(
            apiEndpoints.documents.uploadUrl(),
            {
              key: thumbnailKey,
              folder: 'pending',
              contentType: 'image/jpeg',
              fileSize: thumbnailFile.size, // Required by backend validation
            }
          );
          
          if (thumbUrlResponse.error || !thumbUrlResponse.data) {
            throw new Error(thumbUrlResponse.error || 'Failed to get presigned upload URL for thumbnail');
          }
          
          const thumbBuffer = await fileToBuffer(thumbnailFile);
          const thumbUploadResponse = await fetch(thumbUrlResponse.data.url, {
            method: 'PUT',
            body: thumbBuffer as any, // Uint8Array is compatible with BodyInit at runtime
            headers: {
              'Content-Type': 'image/jpeg',
            },
          });
          
          if (!thumbUploadResponse.ok) {
            throw new Error(`Failed to upload thumbnail to S3: ${thumbUploadResponse.statusText}`);
          }
        } else {
          // Direct S3 upload for thumbnail
          await s3Service.uploadFile(thumbnailFile, thumbnailKey, 'pending');
        }
        
        status.progress = 60;
        status.message = `Thumbnail: ${(thumbnail.size / 1024).toFixed(0)}KB`;
      } catch (error) {
        console.warn('Failed to generate thumbnail:', error);
        if (!pdfPagesMeasured) {
          try {
            pageCount = await countPdfPages(file);
          } catch (countError) {
            console.warn('Failed to count PDF pages:', countError);
            pageCount = 1;
          }
        }
        // Continue without thumbnail - not critical
      }
    }

    // OCR runs on the backend after create (S3 object) when API is configured — same for images and PDFs.
    // Avoids exposing IAM keys in the browser via Textract SDK.
    status.stage = 'extracting';
    status.progress = 70;
    status.message = 'Preparing document...';

    const extractedData: Document['extractedData'] = {
      text: '',
      formFields: [],
      tables: [],
      rawTextractResponse: null,
    };
    const colorType: 'color' | 'grayscale' = 'color';

    // Create document record
    status.progress = 85;
    const document: Document = {
      id: documentId,
      fileName: processedFile.name,
      fileType: isPDFFile(file)
        ? processedFile.type || 'application/pdf'
        : processedFile.type || 'image/png',
      uploadDate: new Date().toISOString(),
      status: 'pending',
      colorType,
      s3Key: finalS3Key,
      thumbnailKey: thumbnailKey,
      pageCount,
      extractedData,
      storedFields: {},
      customFields: [],
      searchableText: processedFile.name,
      auditTrail: [],
      extractionChoice,
      originalFileName: file.name,
      originalByteSize: file.size,
      ...(contentHash ? { contentHash } : {}),
    };

    const createPayload = {
      ...document,
      ...(duplicate?.ewayBillNumbers?.length ? { ewayBillNumbers: duplicate.ewayBillNumbers } : {}),
      ...(duplicate?.documentNumbers?.length ? { documentNumbers: duplicate.documentNumbers } : {}),
      ...(duplicate?.invoiceNumbers?.length ? { invoiceNumbers: duplicate.invoiceNumbers } : {}),
      ...(duplicate?.podNumbers?.length ? { podNumbers: duplicate.podNumbers } : {}),
    };

    if (isApiGatewayConfigured()) {
      const createResponse = await apiClient.post<{ documentId: string; message: string }>(
        apiEndpoints.documents.create(),
        createPayload
      );

      if (createResponse.error || !createResponse.data) {
        const errorMsg = createResponse.error || createResponse.details?.error || 'Failed to save document. Please try again.';
        throw new Error(errorMsg);
      }
      documentRowCreated = true;
    } else {
      await dynamoDBService.createDocument(document);
      documentRowCreated = true;
    }

    // Images: sync Textract via backend. PDFs: async Textract (poll textract-status until complete).
    let readByTextract = false;
    if ((isImageFile(file) || isPDFFile(file)) && isApiGatewayConfigured()) {
      status.progress = 92;
      status.message = isPDFFile(file) ? 'Reading the PDF...' : 'Reading the image...';
      let processResponse = await apiClient.post<ProcessDocumentApiResponse>(
        apiEndpoints.documents.process(documentId),
        { duplicateOverride: billNumbersAcknowledged }
      );
      if (processResponse.error || !processResponse.data) {
        throw processFailure(processResponse);
      }
      let pdata = processResponse.data;
      if (pdata.duplicatePrompt && pdata.matches && pdata.matches.length > 0) {
        const proceed = await requestDuplicateDecision({ fileName: file.name, matches: pdata.matches });
        if (!proceed) {
          throw new UploadCancelledError();
        }
        billNumbersAcknowledged = true;
        processResponse = await apiClient.post<ProcessDocumentApiResponse>(
          apiEndpoints.documents.process(documentId),
          { duplicateOverride: true }
        );
        if (processResponse.error || !processResponse.data) {
          throw processFailure(processResponse);
        }
        pdata = processResponse.data;
      }
      if (pdata.ocrAsync && isPDFFile(file)) {
        readByTextract = true;
        status.message = 'Reading the scanned PDF (large files may take a few minutes)...';
        document.extractedData = await waitForPdfTextract(documentId, (msg) => {
          status.message = msg;
        });
      } else {
        if (!pdata.extractedData) {
          throw new Error('OCR failed for this document. Please try again.');
        }
        document.extractedData = pdata.extractedData;
      }
    }

    if (readByTextract && !billNumbersAcknowledged && isApiGatewayConfigured() && document.extractedData) {
      const check = await checkDuplicateRequest({
        contentHash,
        originalFileName: file.name,
        originalByteSize: file.size,
        pageCount,
        pdfText: document.extractedData.text,
        fields: document.extractedData.formFields,
        excludeDocumentId: documentId,
      });
      const matches = billMatches(check.matches);
      if (matches.length > 0) {
        const proceed = await requestDuplicateDecision({ fileName: file.name, matches });
        if (!proceed) {
          throw new UploadCancelledError();
        }
      }
    }

    status.stage = 'completed';
    status.progress = 100;
    status.message = 'Extraction complete';

    return { documentId, status, document };
    } catch (error: unknown) {
      if (error instanceof UploadCancelledError) {
        try {
          await performUploadFailureCleanup({
            documentId,
            finalS3Key,
            thumbnailKey,
            documentRowCreated,
            failureSummary: error.message,
          });
        } catch {
          throw new UploadCancelledError(
            'This file was not uploaded. Some of it could not be removed automatically. Delete it from the list if it still appears.'
          );
        }
        throw error;
      }
      const detail =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
            ? error
            : 'Upload could not complete.';
      let cleanupParagraph = '';
      try {
        cleanupParagraph = await performUploadFailureCleanup({
          documentId,
          finalS3Key,
          thumbnailKey,
          documentRowCreated,
          failureSummary: detail,
        });
      } catch (cleanupErr) {
        const ce = cleanupErr instanceof Error ? cleanupErr.message : String(cleanupErr);
        cleanupParagraph = `Automatic cleanup did not fully complete (${ce}). A partial document or file may remain in your account; you can delete it from the document list if it appears.`;
      }
      throw new Error(
        `Upload could not finish.\n\nReason:\n${detail}\n\nCleanup:\n${cleanupParagraph}\n\nNext step: Please upload your file again. If this keeps happening, try a smaller file or a different format (JPG, PNG, HEIC, or PDF).`
      );
    }
  },

  /**
   * Get processing status
   * Note: In production, this should be stored in DynamoDB or use SQS
   * For now, returns null as processing is synchronous
   */
  async getProcessingStatus(documentId: string): Promise<ProcessingStatus | null> {
    // Processing status tracking can be implemented with DynamoDB or SQS
    // For now, return null as processing completes synchronously
    return null;
  },

  /**
   * Approve and store a document
   * NOTE: This function does NOT call Textract - it uses the extractedData
   * that was already extracted during upload. Textract is only called in uploadDocument().
   */
  async approveDocument(
    documentId: string,
    storedFields: Record<string, any>,
    customFields: CustomField[]
  ): Promise<Document> {
    await checkThrottle('add');
    // Use API Gateway endpoint (production) or direct DynamoDB (local development)
    const doc = isApiGatewayConfigured()
      ? await this.getDocument(documentId)
      : await dynamoDBService.getDocument(documentId);
    if (!doc) throw new Error('Document not found. It may have been deleted or moved.');

    const currentUser = await authService.getCurrentUser();
    if (!currentUser) throw new Error('You must be logged in to perform this action. Please log in and try again.');

    // Robust server-side approval flow: S3 move + DB update are handled by backend.
    if (isApiGatewayConfigured()) {
      const approveResponse = await apiClient.post<Document>(
        apiEndpoints.documents.approve(documentId),
        { storedFields, customFields }
      );
      if (approveResponse.error || !approveResponse.data) {
        throw new Error(approveResponse.error || 'Failed to approve and store document. Please try again.');
      }
      return approveResponse.data;
    }

    // Standardize dates to ISO 8601
    Object.keys(storedFields).forEach(key => {
      if (key.toLowerCase().includes('date')) {
        try {
          const date = new Date(storedFields[key]);
          if (!isNaN(date.getTime())) {
            storedFields[key] = date.toISOString().split('T')[0];
          }
        } catch (e) {
          // Keep original if parsing fails
        }
      }
      // Add currency to amounts
      if (key.toLowerCase().includes('amount') || key.toLowerCase().includes('total')) {
        const amountValue = storedFields[key];
        if (typeof amountValue === 'string' && !amountValue.includes('INR')) {
          const numMatch = amountValue.match(/[\d,]+\.?\d*/);
          if (numMatch) {
            const num = numMatch[0].replace(/,/g, '');
            storedFields[key] = `${num} INR`;
          }
        }
      }
    });

    const searchableText = buildDocumentSearchableText({
      fileName: doc.fileName,
      storedFields,
      customFields,
      extractedData: doc.extractedData,
    });

    // Move file from pending to active folder in S3 (only if currently in pending)
    // Skip move if document is already active (e.g., editing an already-approved document)
    if (doc.s3Key && doc.status === 'pending') {
      try {
        if (isApiGatewayConfigured()) {
          // Use API Gateway endpoint for moving files
          const moveResponse = await apiClient.post<{ message: string; key: string; fromFolder: string; toFolder: string }>(
            apiEndpoints.documents.moveFile(documentId),
            {
              key: doc.s3Key,
              fromFolder: 'pending',
              toFolder: 'active',
            }
          );
          
          if (moveResponse.error) {
            // If file doesn't exist in pending, check if it's already in active
            if (moveResponse.error.includes('not found') || moveResponse.error.includes('NoSuchKey') || moveResponse.details?.status === 404) {
              // Silently handle - file likely already in active folder
              // Only log at debug level to avoid console noise
              if (process.env.NODE_ENV === 'development') {
                console.info(`File ${doc.s3Key} not found in pending folder, checking active folder...`);
              }
              // Verify S3 state before allowing the status flip to active.
              try {
                const existsResp = await apiClient.get<{ exists: boolean }>(
                  apiEndpoints.documents.storageExists(documentId, { folder: 'active', kind: 'original' })
                );
                if (existsResp.error || !existsResp.data?.exists) {
                  throw new Error('Document file not found in storage. The file may have been deleted. Please re-upload the document.');
                }
                if (process.env.NODE_ENV === 'development') {
                  console.info('File exists in active folder, skipping move');
                }
              } catch (activeError) {
                throw new Error('Document file not found in storage. The file may have been deleted. Please re-upload the document.');
              }
            } else {
              throw new Error(moveResponse.error || 'Failed to move document file. Please try again.');
            }
          }
        } else {
          // Direct S3 call (local development with AWS credentials)
          await s3Service.moveFile(doc.s3Key, 'pending', 'active');
        }
      } catch (error: any) {
        // If file doesn't exist in pending, check if it's already in active
        if (error.message?.includes('NoSuchKey') || error.message?.includes('does not exist') || error.message?.includes('not found')) {
          // Silently handle - file likely already in active folder
          // Only log at debug level to avoid console noise
          if (process.env.NODE_ENV === 'development') {
            console.info(`File ${doc.s3Key} not found in pending folder, checking active folder...`);
          }
          // Try to verify if file exists in active folder
          try {
            if (isApiGatewayConfigured()) {
              const existsResp = await apiClient.get<{ exists: boolean }>(
                apiEndpoints.documents.storageExists(documentId, { folder: 'active', kind: 'original' })
              );
              if (existsResp.error || !existsResp.data?.exists) {
                throw new Error('Document file not found in storage. The file may have been deleted. Please re-upload the document.');
              }
            } else {
              await s3Service.getPresignedUrl(doc.s3Key, 'active');
            }
            // File exists in active, no need to move - this is fine, continue
            if (process.env.NODE_ENV === 'development') {
              console.info('File already in active folder, skipping move');
            }
          } catch (activeError) {
            // File doesn't exist in active either, this is a real error
            throw new Error('Document file not found in storage. The file may have been deleted. Please re-upload the document.');
          }
        } else {
          throw error;
        }
      }
    }
    
    // Move thumbnail if it exists and document is pending
    if (doc.thumbnailKey && doc.status === 'pending') {
      try {
        if (isApiGatewayConfigured()) {
          // Use API Gateway endpoint for moving thumbnail
          const moveResponse = await apiClient.post<{ message: string; key: string; fromFolder: string; toFolder: string }>(
            apiEndpoints.documents.moveFile(documentId),
            {
              key: doc.thumbnailKey,
              fromFolder: 'pending',
              toFolder: 'active',
            }
          );
          
          if (moveResponse.error) {
            // Non-critical error for thumbnails, just log
            console.warn('Failed to move thumbnail:', moveResponse.error);
          }
        } else {
          // Direct S3 call (local development with AWS credentials)
          await s3Service.moveFile(doc.thumbnailKey, 'pending', 'active');
        }
      } catch (error: any) {
        // If thumbnail doesn't exist in pending, check if it's already in active
        if (error.message?.includes('NoSuchKey') || error.message?.includes('does not exist') || error.message?.includes('not found')) {
          console.warn(`Thumbnail ${doc.thumbnailKey} not found in pending folder, checking active folder...`);
          try {
            if (isApiGatewayConfigured()) {
              const existsResp = await apiClient.get<{ exists: boolean }>(
                apiEndpoints.documents.storageExists(documentId, { folder: 'active', kind: 'thumbnail' })
              );
              if (existsResp.error || !existsResp.data?.exists) {
                throw new Error('Thumbnail file not found in storage.');
              }
            } else {
              await s3Service.getPresignedUrl(doc.thumbnailKey, 'active');
            }
            // Thumbnail exists in active, no need to move
            console.log('Thumbnail already in active folder, skipping move');
          } catch (activeError) {
            // Thumbnail doesn't exist, but this is not critical - just log
            console.warn('Thumbnail not found, continuing without thumbnail');
          }
        } else {
          // Non-critical error for thumbnails, just log
          console.warn('Failed to move thumbnail:', error);
        }
      }
    }

    // Update document via API Gateway (production) or directly (local development)
    // Note: 'created' audit entry is already added during document creation in createDocument.ts
    // This update will add an 'edited' audit entry with the changes (status, storedFields, customFields)
    const updatedDoc = await this.updateDocument(documentId, {
      status: 'active',
      storedFields,
      customFields,
      searchableText,
    });

    return updatedDoc;
  },

  /**
   * Reject a document (move to deleted items)
   */
  async rejectDocument(documentId: string): Promise<void> {
    // Production uses DELETE /documents/:id (soft delete): S3 moves + DynamoDB status + audit are server-side.
    // Local/dev without API uses direct DynamoDB + S3 SDK (same outcome as approve's split).
    if (isApiGatewayConfigured()) {
      await this.deleteDocument(documentId, false);
      return;
    }

    const doc = await dynamoDBService.getDocument(documentId);
    if (!doc) return;

    const currentUser = await authService.getCurrentUser();
    if (!currentUser) throw new Error('You must be logged in to perform this action. Please log in and try again.');

    // Move file to deleted folder
    if (doc.s3Key) {
      await s3Service.moveFile(doc.s3Key, 'pending', 'deleted');
    }
    // Move thumbnail if it exists
    if (doc.thumbnailKey) {
      await s3Service.moveFile(doc.thumbnailKey, 'pending', 'deleted');
    }

    // Update status to deleted
    await dynamoDBService.updateDocument(documentId, { status: 'deleted' });

    // Add audit trail
    const auditEntry = {
      id: `audit-${Date.now()}`,
      documentId,
      action: 'deleted' as const,
      userId: currentUser.id,
      timestamp: new Date().toISOString(),
    };
    doc.auditTrail.push(auditEntry);
    await dynamoDBService.updateDocument(documentId, { auditTrail: doc.auditTrail });
  },

  /**
   * Update a document
   * NOTE: This function does NOT call Textract - it only updates the document metadata.
   * Textract is only called during initial upload in uploadDocument().
   * The extractedData field should not be modified here - it's preserved from the original upload.
   */
  async updateDocument(documentId: string, updates: Partial<Document>): Promise<Document> {
    await checkThrottle('update');
    
    // Ensure extractedData is not accidentally overwritten during updates
    // If updates contain extractedData, preserve the original (extractedData should never change after upload)
    const doc = await (isApiGatewayConfigured() 
      ? apiService.getDocument(documentId)
      : dynamoDBService.getDocument(documentId));
    
    if (doc && updates.extractedData && updates.extractedData !== doc.extractedData) {
      console.warn('Warning: Attempted to modify extractedData during update. Preserving original extractedData from upload.');
      // Remove extractedData from updates to preserve the original
      const { extractedData, ...safeUpdates } = updates;
      updates = safeUpdates;
    }
    
    // If storedFields or customFields are being updated, regenerate searchableText (include OCR/tables)
    if (updates.storedFields || updates.customFields) {
      const currentDoc = doc || await (isApiGatewayConfigured() 
        ? apiService.getDocument(documentId)
        : dynamoDBService.getDocument(documentId));
      
      if (currentDoc) {
        const updatedStoredFields = updates.storedFields || currentDoc.storedFields || {};
        const updatedCustomFields = updates.customFields || currentDoc.customFields || [];

        updates.searchableText = buildDocumentSearchableText({
          fileName: currentDoc.fileName,
          storedFields: updatedStoredFields as Record<string, unknown>,
          customFields: updatedCustomFields as CustomField[],
          extractedData: currentDoc.extractedData,
        });
      }
    }
    
    if (isApiGatewayConfigured()) {
      // Use API Gateway
      const response = await apiClient.put<Document>(
        apiEndpoints.documents.update(documentId),
        updates
      );
      
      if (response.error) {
        // Use detailed error message if available, otherwise use generic error
        const errorMessage = response.details?.error || response.error || 'Failed to update document';
        throw new Error(errorMessage);
      }
      
      return response.data!;
    } else {
      // Fallback to direct DynamoDB
      return await dynamoDBService.updateDocument(documentId, updates);
    }
  },

  /**
   * Delete a document (soft or permanent)
   */
  async deleteDocument(documentId: string, permanent: boolean = false): Promise<void> {
    await checkThrottle('delete');
    
    if (isApiGatewayConfigured()) {
      // Use API Gateway (handles both soft and permanent delete)
      const response = await apiClient.delete<{ message: string }>(
        apiEndpoints.documents.delete(documentId, permanent)
      );
      
      if (response.error) {
        throw new Error(response.error);
      }
    } else {
      // Fallback to direct AWS SDK
      const doc = await dynamoDBService.getDocument(documentId);
      if (!doc) return;

      const currentUser = await authService.getCurrentUser();
      if (!currentUser) throw new Error('You must be logged in to perform this action. Please log in and try again.');

      // Helper to determine source folder based on document status
      const getSourceFolder = (status: string): string => {
        if (status === 'deleted') return 'deleted';
        if (status === 'pending') return 'pending';
        return 'active';
      };

      if (permanent) {
        // Delete from S3
        if (doc.s3Key) {
          const folder = getSourceFolder(doc.status);
          await s3Service.deleteFile(doc.s3Key, folder);
        }
        // Delete thumbnail if it exists
        if (doc.thumbnailKey) {
          const folder = getSourceFolder(doc.status);
          await s3Service.deleteFile(doc.thumbnailKey, folder);
        }
        // Delete from DynamoDB
        await dynamoDBService.deleteDocument(documentId);
      } else {
        // Soft delete: move to deleted folder
        if (doc.s3Key && doc.status !== 'deleted') {
          const sourceFolder = getSourceFolder(doc.status);
          await s3Service.moveFile(doc.s3Key, sourceFolder, 'deleted');
        }
        // Move thumbnail if it exists
        if (doc.thumbnailKey && doc.status !== 'deleted') {
          const sourceFolder = getSourceFolder(doc.status);
          await s3Service.moveFile(doc.thumbnailKey, sourceFolder, 'deleted');
        }
        await dynamoDBService.updateDocument(documentId, { status: 'deleted' });

        // Add audit trail
        const auditEntry = {
          id: `audit-${Date.now()}`,
          documentId,
          action: 'deleted' as const,
          userId: currentUser.id,
          timestamp: new Date().toISOString(),
        };
        doc.auditTrail.push(auditEntry);
        await dynamoDBService.updateDocument(documentId, { auditTrail: doc.auditTrail });
      }
    }
  },

  /**
   * Restore a document from deleted items
   */
  async restoreDocument(documentId: string): Promise<Document> {
    // Production: use API Gateway to perform restore (no direct DynamoDB/S3 calls from frontend)
    if (isApiGatewayConfigured()) {
      const currentUser = await authService.getCurrentUser();
      if (!currentUser) throw new Error('You must be logged in to perform this action. Please log in and try again.');

      const response = await apiClient.post<{ message: string; document: Document }>(
        apiEndpoints.documents.restore(documentId)
      );

      if (response.error || !response.data?.document) {
        const errorMsg = response.error || response.details?.error || 'Failed to restore document. Please try again.';
        throw new Error(errorMsg);
      }

      return response.data.document;
    }

    // Local development fallback: direct DynamoDB/S3 operations
    const doc = await dynamoDBService.getDocument(documentId);
    if (!doc) throw new Error('Document not found. It may have been deleted or moved.');

    const currentUser = await authService.getCurrentUser();
    if (!currentUser) throw new Error('You must be logged in to perform this action. Please log in and try again.');

    // Move file back to active folder
    if (doc.s3Key) {
      await s3Service.moveFile(doc.s3Key, 'deleted', 'active');
    }
    // Move thumbnail if it exists
    if (doc.thumbnailKey) {
      await s3Service.moveFile(doc.thumbnailKey, 'deleted', 'active');
    }

    // Update status
    const updatedDoc = await dynamoDBService.updateDocument(documentId, {
      status: 'active',
    });

    // Add audit trail
    const auditEntry = {
      id: `audit-${Date.now()}`,
      documentId,
      action: 'restored' as const,
      userId: currentUser.id,
      timestamp: new Date().toISOString(),
    };
    updatedDoc.auditTrail.push(auditEntry);
    await dynamoDBService.updateDocument(documentId, { auditTrail: updatedDoc.auditTrail });

    return updatedDoc;
  },

  /**
   * Log an action for audit trail
   */
  async logAction(documentId: string, action: string, details?: Record<string, any>): Promise<void> {
    try {
      const currentUser = await authService.getCurrentUser();
      if (!currentUser) return;

      if (isApiGatewayConfigured()) {
        // Use API Gateway to get and update document
        const doc = await this.getDocument(documentId);
        if (doc) {
          const auditEntry = {
            id: `audit-${Date.now()}`,
            documentId,
            action: action as any,
            userId: currentUser.id,
            timestamp: new Date().toISOString(),
            ...(details && { details }),
          };
          const updatedAuditTrail = [...(doc.auditTrail || []), auditEntry];
          await this.updateDocument(documentId, { auditTrail: updatedAuditTrail });
        }
      } else {
        // Fallback to direct DynamoDB (local development only)
        const doc = await dynamoDBService.getDocument(documentId);
        if (doc) {
          const auditEntry = {
            id: `audit-${Date.now()}`,
            documentId,
            action: action as any,
            userId: currentUser.id,
            timestamp: new Date().toISOString(),
            ...(details && { details }),
          };
          doc.auditTrail.push(auditEntry);
          await dynamoDBService.updateDocument(documentId, { auditTrail: doc.auditTrail });
        }
      }
    } catch (error) {
      // Log action failures are non-critical - don't break the UI
      console.warn('Failed to log action (non-critical):', error);
    }
  },

  /**
   * Get global audit log entries (flattened across all documents)
   */
  async getAuditLog(): Promise<AuditLogEntry[]> {
    // Fetch documents in API-compatible pages (backend enforces limit: 1..100)
    // and aggregate all audit entries across pages.
    const allDocs: Document[] = [];
    let nextToken: string | undefined;
    const pageLimit = 100;

    do {
      const { documents, nextToken: newNextToken } = await this.getDocuments(
        { status: ['pending', 'active', 'deleted'] },
        { limit: pageLimit, nextToken }
      );
      allDocs.push(...documents);
      nextToken = newNextToken;
    } while (nextToken);

    const entries: AuditLogEntry[] = [];
    allDocs.forEach((doc) => {
      (doc.auditTrail || []).forEach((entry) => {
        entries.push({
          ...entry,
          documentFileName: doc.fileName,
          documentStatus: doc.status,
        });
      });
    });

    entries.sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

    return entries;
  },

  /**
   * Get presigned URL for document preview/download
   * @param documentId - Document ID
   * @param useThumbnail - If true, returns thumbnail URL (smaller file, ~8-15KB). If false or thumbnail unavailable, returns full image (~300-800KB)
   * @param skipThrottle - If true, skips throttling check (used for thumbnail loading in card views)
   * @param abortSignal - When aborted (e.g. card unmounted after permanent delete), returns '' without treating as error
   */
  async getDocumentUrl(
    documentId: string,
    useThumbnail: boolean = false,
    skipThrottle: boolean = false,
    abortSignal?: AbortSignal
  ): Promise<string> {
    if (abortSignal?.aborted) {
      return '';
    }
    // Only throttle actual downloads, not thumbnail loads for card previews
    // Thumbnails are loaded automatically for every card, so throttling would hit limits quickly
    // Actual downloads (when user clicks download button) should still be throttled
    if (!skipThrottle && !useThumbnail) {
      await checkThrottle('download');
    }
    
    if (isApiGatewayConfigured()) {
      // Use API Gateway endpoint
      const params = new URLSearchParams();
      if (useThumbnail) {
        params.append('thumbnail', 'true');
      }
      const query = params.toString();
      const endpoint = `${apiEndpoints.documents.getUrl(documentId)}${query ? `?${query}` : ''}`;
      
      const response = await apiClient.get<{ url: string; expiresIn: number }>(endpoint, {
        signal: abortSignal,
      });
      
      if (response.error === 'aborted') {
        return '';
      }

      if (response.error) {
        // Provide user-friendly error messages
        let errorMsg = response.error;
        const errorLower = response.error.toLowerCase();
        const isFileNotFound =
          errorLower.includes('not found') ||
          response.error.includes('NoSuchKey') ||
          response.error.includes('404') ||
          errorLower.includes('file not found') ||
          errorLower.includes('document file not found');

        const isThumbnailUnavailable =
          errorLower.includes('thumbnail not available') ||
          errorLower.includes('thumbnail has not been generated');
        
        if (isFileNotFound) {
          errorMsg = 'Document file not found. The file may have been deleted or never uploaded.';
          // Don't throw for missing files - let the preview component handle it gracefully
          // Silently handle - no console logging to avoid noise
          // Return empty string so preview component can show fallback UI
          return '';
        }

        if (isThumbnailUnavailable) {
          // Thumbnails may legitimately not exist yet; treat as non-fatal and let UI show fallback
          return '';
        }

        if (response.error.includes('AccessDenied') || response.error.includes('403')) {
          errorMsg = 'You do not have permission to view this document.';
        } else if (response.error.includes('500') || response.error.includes('Internal Server Error')) {
          errorMsg = 'Unable to load document preview. Please try again in a few moments.';
        }
        throw new Error(errorMsg);
      }
      
      if (!response.data?.url) {
        throw new Error('Failed to get document URL. Please try again.');
      }
      
      return response.data.url;
    } else {
      // Fallback to direct AWS SDK (requires credentials)
      const doc = await dynamoDBService.getDocument(documentId);
      if (!doc) throw new Error('Document not found. It may have been deleted or moved.');

      // Use thumbnail if requested and available, otherwise use full image
      const key = useThumbnail && doc.thumbnailKey ? doc.thumbnailKey : doc.s3Key;
      if (!key) throw new Error('Document S3 key not found');

      // Determine folder based on document status
      let folder: string;
      if (doc.status === 'deleted') {
        folder = 'deleted';
      } else if (doc.status === 'pending') {
        folder = 'pending';
      } else {
        folder = 'active';
      }
      
      return await s3Service.getPresignedUrl(key, folder);
    }
  },

  /**
   * One-Time Activities
   */
  async listOneTimeActions(): Promise<OneTimeAction[]> {
    if (isApiGatewayConfigured()) {
      console.log('[apiService] Calling listOneTimeActions API...');
      const response = await apiClient.get<{ actions: OneTimeAction[] }>(
        apiEndpoints.oneTimeActions.list()
      );
      
      console.log('[apiService] API response:', response);
      
      if (response.error) {
        console.error('[apiService] API error:', response.error);
        throw new Error(response.error);
      }
      
      const actions = response.data?.actions || [];
      console.log('[apiService] Parsed actions:', actions);
      console.log('[apiService] Actions count:', actions.length);
      
      return actions;
    } else {
      throw new Error('One-Time Actions require API Gateway configuration');
    }
  },

  async initOneTimeActions(): Promise<{ message: string; createdActions: string[]; totalActions: number }> {
    if (isApiGatewayConfigured()) {
      console.log('[apiService] Calling initOneTimeActions API...');
      const response = await apiClient.post<{ message: string; createdActions: string[]; totalActions: number }>(
        apiEndpoints.oneTimeActions.init()
      );
      
      console.log('[apiService] Init API response:', response);
      
      if (response.error || !response.data) {
        console.error('[apiService] Init API error:', response.error);
        throw new Error(response.error || 'Failed to initialize one-time actions');
      }
      
      console.log('[apiService] Init result:', response.data);
      return response.data;
    } else {
      throw new Error('One-Time Actions require API Gateway configuration');
    }
  },

  async startOneTimeAction(actionId: string, filters?: Record<string, any>): Promise<{ runId: string; status: string }> {
    if (isApiGatewayConfigured()) {
      const response = await apiClient.post<{ runId: string; status: string }>(
        apiEndpoints.oneTimeActions.start(actionId),
        { filters }
      );
      
      if (response.error || !response.data) {
        throw new Error(response.error || 'Failed to start one-time action');
      }
      
      return response.data;
    } else {
      throw new Error('One-Time Actions require API Gateway configuration');
    }
  },

  async stopOneTimeAction(actionId: string): Promise<{ runId: string; status: string; message: string }> {
    if (isApiGatewayConfigured()) {
      const response = await apiClient.post<{ runId: string; status: string; message: string }>(
        apiEndpoints.oneTimeActions.stop(actionId)
      );
      
      if (response.error || !response.data) {
        throw new Error(response.error || 'Failed to stop one-time action');
      }
      
      return response.data;
    } else {
      throw new Error('One-Time Actions require API Gateway configuration');
    }
  },

  async getOneTimeActionStatus(actionId: string): Promise<{
    actionId: string;
    hasActiveRun: boolean;
    status: string | null;
    run?: {
      id: string;
      status: string;
      progress: number;
      totalItems: number;
      processedItems: number;
      succeededItems: number;
      failedItems: number;
      startedAt: string;
      completedAt?: string;
      error?: string;
    };
    lastRun?: {
      id: string;
      status: string;
      progress: number;
      totalItems: number;
      processedItems: number;
      succeededItems: number;
      failedItems: number;
      startedAt: string;
      completedAt?: string;
      error?: string;
    };
  }> {
    if (isApiGatewayConfigured()) {
      const response = await apiClient.get<{
        actionId: string;
        hasActiveRun: boolean;
        status: string | null;
        run?: {
          id: string;
          status: string;
          progress: number;
          totalItems: number;
          processedItems: number;
          succeededItems: number;
          failedItems: number;
          startedAt: string;
          completedAt?: string;
          error?: string;
        };
        lastRun?: {
          id: string;
          status: string;
          progress: number;
          totalItems: number;
          processedItems: number;
          succeededItems: number;
          failedItems: number;
          startedAt: string;
          completedAt?: string;
          error?: string;
        };
      }>(
        apiEndpoints.oneTimeActions.status(actionId)
      );
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      return response.data || { actionId, hasActiveRun: false, status: null };
    } else {
      throw new Error('One-Time Actions require API Gateway configuration');
    }
  },

  async getOneTimeActionHistory(actionId: string): Promise<{
    actionId: string;
    runs: OneTimeActionRunSummary[];
  }> {
    if (isApiGatewayConfigured()) {
      const response = await apiClient.get<{
        actionId: string;
        runs: OneTimeActionRunSummary[];
      }>(
        apiEndpoints.oneTimeActions.history(actionId)
      );
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      return response.data || { actionId, runs: [] };
    } else {
      throw new Error('One-Time Actions require API Gateway configuration');
    }
  },
};

